/**
 * MusicArtistDetailPage - アーティスト詳細（そのアーティストのアルバム一覧）
 *
 * - アーティスト名はヘッダーに表示し、上部のアーティスト画像は表示しない
 * - アルバムはリリース年の新しい順に並べる
 * - 再生 / シャッフル はそのアーティストの全曲をキューに入れる
 */

import { Disc3, Play, Shuffle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { getMusicAlbums, getQueueTracks } from '@/api/api'
import type { MusicAlbumItem, QueueTrackItem } from '@/api/type'
import { Header } from '@/components/shared/header'
import { AuthImage } from '@/components/ui/auth-image'
import { Spinner } from '@/components/ui/spinner'
import { parseUtcDate } from '@/lib/date-utils'
import { PATHS } from '@/routes/paths'
import { useMusicStore } from '@/store/useMusicStore'

import { ACCENT_COLOR, DOWNLOADED_ONLY_PARAM, DOWNLOADED_ONLY_QUERY, TRACK_FETCH_LIMIT } from './constants'
import { formatReleaseYear } from './format'
import { buildOfflineAlbums, findOfflineArtistName, useOfflineMusicTracks } from './offline-music'
import { pickRandomTracks, sliceQueueWindow, toMusicTrack } from './queue-track'

export const MusicArtistDetailPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const playQueue = useMusicStore((s) => s.playQueue)
  const { tag_id: tagIdParam } = useParams<{ tag_id: string }>()
  const tagId = Number(tagIdParam)
  const offlineTracks = useOfflineMusicTracks()

  // ダウンロード済みライブラリからの遷移では、オフライン保存済みのアルバムだけを表示する
  const isDownloadedOnly = searchParams.get(DOWNLOADED_ONLY_PARAM) === '1'

  const [albums, setAlbums] = useState<MusicAlbumItem[]>([])
  const [artistName, setArtistName] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!Number.isFinite(tagId)) {
      setError('アーティストが指定されていません')
      setIsLoading(false)
      return
    }

    // ダウンロード済み表示は IndexedDB だけで組み立てられるためサーバーに問い合わせない
    if (isDownloadedOnly) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setError(null)
    getMusicAlbums(tagId)
      .then((res) => {
        if (cancelled) return
        setAlbums(res.albums)
        setArtistName(res.artist_name ?? '')
      })
      .catch(() => {
        if (cancelled) return
        setError('アルバムの取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tagId, isDownloadedOnly])

  const offlineAlbums = useMemo(
    () => (offlineTracks === null ? null : buildOfflineAlbums(offlineTracks, tagId)),
    [offlineTracks, tagId],
  )

  const sortedAlbums = useMemo(() => {
    const base = isDownloadedOnly ? (offlineAlbums ?? []) : albums
    const collator = new Intl.Collator('ja')
    return [...base].sort((a, b) => {
      const aTime = a.released_at ? parseUtcDate(a.released_at).getTime() : 0
      const bTime = b.released_at ? parseUtcDate(b.released_at).getTime() : 0
      const diff = bTime - aTime
      if (diff !== 0) return diff
      return collator.compare(a.title, b.title)
    })
  }, [albums, isDownloadedOnly, offlineAlbums])

  const displayName = isDownloadedOnly && offlineTracks ? findOfflineArtistName(offlineTracks, tagId) : artistName

  const isPending = isDownloadedOnly ? offlineAlbums === null : isLoading

  /** このアーティストの全曲を取得する（ダウンロード済み表示では IndexedDB から組み立てる） */
  const loadArtistTracks = async (): Promise<QueueTrackItem[]> => {
    if (isDownloadedOnly) {
      return (offlineTracks ?? [])
        .filter((t) => t.artist_tag_id === tagId)
        .sort((a, b) => a.product_id - b.product_id || a.episode_id - b.episode_id)
    }

    const res = await getQueueTracks({ source: 'artist', tag_id: tagId, limit: TRACK_FETCH_LIMIT })
    return res.tracks
  }

  const handlePlayAll = () => {
    void loadArtistTracks()
      .then((tracks) => {
        if (tracks.length === 0) return
        playQueue(sliceQueueWindow(tracks, 0).items.map(toMusicTrack), 0)
      })
      .catch(() => {
        // 取得に失敗した場合は再生を開始しない（アルバム一覧の表示は維持する）
      })
  }

  const handleShuffle = () => {
    void loadArtistTracks()
      .then((tracks) => {
        if (tracks.length === 0) return
        playQueue(pickRandomTracks(tracks).map(toMusicTrack), 0, { shuffle: true })
      })
      .catch(() => {
        // 取得に失敗した場合は再生を開始しない（アルバム一覧の表示は維持する）
      })
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header
        title={displayName}
        onClose={() =>
          navigate(isDownloadedOnly ? `${PATHS.MUSIC.ARTISTS}?${DOWNLOADED_ONLY_QUERY}` : PATHS.MUSIC.ARTISTS)
        }
      />

      <div className="flex shrink-0 gap-3 px-4 py-3">
        <button
          type="button"
          onClick={handlePlayAll}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-none bg-white/10 py-3 font-semibold"
          style={{ color: ACCENT_COLOR }}
        >
          <Play size={18} fill={ACCENT_COLOR} />
          再生
        </button>
        <button
          type="button"
          onClick={handleShuffle}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border-none bg-white/10 py-3 font-semibold"
          style={{ color: ACCENT_COLOR }}
        >
          <Shuffle size={18} />
          シャッフル
        </button>
      </div>

      {isPending && (
        <div className="flex flex-1 justify-center py-12">
          <Spinner size={40} />
        </div>
      )}
      {!isPending && error && <div className="flex-1 py-12 text-center text-muted-foreground">{error}</div>}
      {!isPending && !error && sortedAlbums.length === 0 && (
        <div className="flex-1 py-12 text-center text-muted-foreground">アルバムがありません</div>
      )}
      {!isPending && !error && sortedAlbums.length > 0 && (
        <ul className="grid min-h-0 flex-1 grid-cols-2 gap-x-4 gap-y-5 overflow-y-auto px-4 pb-4">
          {sortedAlbums.map((album) => (
            <li key={album.product_id}>
              <button
                type="button"
                onClick={() => {
                  const path = PATHS.MUSIC.ALBUM_DETAIL.replace(':product_id', String(album.product_id))
                  navigate(isDownloadedOnly ? `${path}?${DOWNLOADED_ONLY_QUERY}` : path)
                }}
                className="w-full border-none text-left"
              >
                <span className="block aspect-square w-full overflow-hidden rounded-md bg-white/10">
                  {album.thumbnail ? (
                    <AuthImage
                      src={album.thumbnail}
                      alt={album.title}
                      className="size-full object-cover"
                      cacheKey={`music-album-${album.product_id}`}
                    />
                  ) : (
                    <span className="flex size-full items-center justify-center">
                      <Disc3 size={28} className="text-muted-foreground" />
                    </span>
                  )}
                </span>
                <span className="mt-2 block truncate text-sm">{album.title}</span>
                <span className="block text-sm text-muted-foreground">{formatReleaseYear(album.released_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
